import {
  nativeCreditPoolFetcher,
  nativeCreditPoolYieldKey,
} from '../../yields/intrinsic/fetchers/nativeCreditPool'
import type { SavingsRegistryEntry } from './registry'

/**
 * Native Credit Pool — wNLP entries for the savings provider.
 *
 * ## What these are
 *
 * Native runs a single-sided market-maker funding pool: LPs deposit one
 * asset, and whitelisted, KYB'd market makers draw that inventory as an
 * off-chain-granted USD credit line to quote on Native Core's CLOB. LP
 * yield is a rebate on relay trading fees, not loan interest.
 *
 * That makes it a **savings vault, not a lender** — there is no
 * user-facing borrow surface, no user collateral, and no utilization
 * curve to model. The closest existing entry is Maple's `syrupUSDC`:
 * undercollateralized institutional credit, off-chain yield, delayed
 * exit. Price the same trust assumption in — the share price is an
 * operator-pushed rebase on the underlying NTLP with no on-chain
 * solvency invariant tying it to CreditVault holdings.
 *
 * ## Shape
 *
 * `wNLP` is the public, non-rebasing ERC-20 wrapper over `NTLP` (the
 * rebasing pool share) — see [readers/nativeWnlp.ts](./readers/nativeWnlp.ts)
 * for why it needs a bespoke reader. Exit is two-legged, hence
 * `withdrawalMode: 'fee-or-queued'`:
 *  - **instant** — `instantRedeem`, capped by the chain's CreditVault
 *    inventory, charging `withdrawFeeBps` (100 bps default).
 *  - **queued** — `WithdrawQueue.requestWithdrawal` then
 *    `claimWithdrawal` after `withdrawalWindow`, at no fee.
 *
 * ## Provenance
 *
 * Every row below was read on-chain (2026-07-30) from Native's published
 * address tables: 108/108 wNLPs live, all `underlying()` and
 * `withdrawQueue()` values matching the docs, and every `NTLP.creditVault()`
 * on every chain matching {@link NATIVE_CREDIT_VAULT}. Symbols are the
 * on-chain `symbol()`; share decimals equal underlying decimals on all
 * 108. Deposit/instant-redeem/queued-redeem were fork-verified end to end
 * on Base (permissionless from a fresh EOA, exact 1 % instant haircut,
 * full value after the 3-day queue).
 *
 * Unfunded rows are kept deliberately — Native seeds a wNLP per listed
 * asset ahead of flow, and an empty pool still reports a valid share
 * price. They cost one multicall slice each and start reporting the
 * moment they are funded.
 */

/** The single CreditVault per chain — the settlement inventory the
 *  router swaps against, and the cap on any instant redemption. Each is
 *  cross-checked against `NTLP.creditVault()` for every pool on the chain. */
export const NATIVE_CREDIT_VAULT: { [chainId: string]: string } = {
  '1': '0xe3d41d19564922c9952f692c5dd0563030f5f2ef', // Ethereum
  '56': '0xba8db0caf781cac69b6acf6c848ac148264cc05d', // BNB Chain
  '42161': '0xba1cf8a63227b46575af823beb4d83d1025eff09', // Arbitrum
  '8453': '0x74a4cd023e5afb88369e3f22b02440f2614a1367', // Base
  '143': '0xcd1d2d602c3e7394515daae96e4ffe16de71e5b4', // Monad
  '196': '0x4df7557734b382eb542bea6c74786d398df4cc19', // X Layer
  '4663': '0x57b8f68ef57af2db70bc9aac891836661ca4cb51', // Robinhood Chain
  '2818': '0x4df7557734b382eb542bea6c74786d398df4cc19', // Morph
}

/**
 * Compact row — the registry is 108 entries and every other field is
 * uniform across them, so only what varies is tabulated:
 * `[symbol, wNLP, underlying, withdrawQueue, decimals, windowSeconds]`.
 *
 * `windowSeconds` is a pinned fallback for display before the on-chain
 * read lands; the reader overrides it with the live
 * `WithdrawQueue.withdrawalWindow()`.
 */
type NativeWnlpRow = readonly [
  symbol: string,
  address: string,
  underlying: string,
  withdrawQueue: string,
  decimals: number,
  withdrawalWindowSeconds: number,
]

const NATIVE_WNLP_ROWS: { [chainId: string]: readonly NativeWnlpRow[] } = {
  // Ethereum — 24 pools, 7 funded, 6 with a DefiLlama APR.
  '1': [
    [
      'wNLP-WETH',
      '0x50ecabe78ae99bb1adc292a94e535feb0ed29853',
      '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
      '0x79dcfeb6f52977ef899d92bece132e95c3d08b66',
      18,
      259200,
    ],
    [
      'wNLP-WBTC',
      '0x9441f31ab75f466a8afdc67a0fa65241f7600f5a',
      '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      '0x0a1a4d7cb85b231cc9223730d2ae0582ddad228a',
      8,
      259200,
    ],
    [
      'wNLP-USDT',
      '0xc31daeeb822790e9ca730e7e34a9ef497fffc959',
      '0xdac17f958d2ee523a2206206994597c13d831ec7',
      '0xd1f2d929881abb26e5780d202d3c550df264791c',
      6,
      259200,
    ],
    [
      'wNLP-USDC',
      '0x3c339289baa2eab3802c1f0c10e025b29bdbea1a',
      '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
      '0x231c3946b3a4ea1163744f236a184f796a8e18a1',
      6,
      259200,
    ],
    [
      'wNLP-STONEUSD',
      '0xb88791ef86d10037f7481b4e3fcbca5bb4162bfa',
      '0x6a6e3a4396993a4ec98a6f4a654cc0819538721e',
      '0x65dd5953ac28018207da0a309d0304cd53e5b734',
      18,
      28800,
    ], // 8h queue, 0bips instant fee
    [
      'wNLP-CRCLon',
      '0xaeb7c9122d897a224180d2ca25bc86a16d12ae92',
      '0x3632dea96a953c11dac2f00b4a05a32cd1063fae',
      '0x5b2bc2d8ad715391aff2ffa0f3f19d3a4c6686ed',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-GOOGLon',
      '0x66924f881bd0cbfabded19838c5f5443867359d9',
      '0xba47214edd2bb43099611b208f75e4b42fdcfedc',
      '0x6285d06426eef9beef33ee92eab7f491f736da57',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-INTCon',
      '0xb5cebfa986dd20a674be725bacebabec458abc84',
      '0xfda09936dbd717368de0835ba441d9e62069d36f',
      '0x97c156ec88085e8a057955908d056e55fc0ac589',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-MRVLon',
      '0x3b1dbe5ca2d39fac168e3575d707b714a7eaa7e7',
      '0xf404e5f887dbd5508e16a1198fcdd5de1a4296b8',
      '0x7f24d9dc37483a50fcbabdb2274cf8748ae0907a',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-MUon',
      '0x1e42d39df3e927c0765f258a8b7f75726350f41b',
      '0x050362ab1072cb2ce74d74770e22a3203ad04ee5',
      '0x3ee49a51a5efa599ff544fd37f197bbf15536e04',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-NVDAon',
      '0xe74ee33a50a9eab6f62cb22f44b1e72e5579894c',
      '0x2d1f7226bd1f780af6b9a49dcc0ae00e8df4bdee',
      '0x3f527be145afa0e808c41ec9060513dbb82afe0c',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-QQQon',
      '0xe05f1635756b6f5046e53f8e911a14794a73a4e7',
      '0x0e397938c1aa0680954093495b70a9f5e2249aba',
      '0x9d2f7e285557c65416fb2bbb268d509e9e2eb2b3',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-SPCXon',
      '0xbffaaa25d2b8fe6e3e8c2c790673fa0c28c64bd4',
      '0xc9eef266834730340a55b6cc24621b31baf55581',
      '0xe66430ce224b1e65100d1b9a15381aa59e17e4b4',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-TSLAon',
      '0x0183d055c77310af03dcb397efaa7e9cfb6db59b',
      '0xf6b1117ec07684d3958cad8beb1b302bfd21103f',
      '0x0cb46d89ed4ae005160b3270fafe453c43bba664',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-CRCLx',
      '0x2ba837484e8969da87d237effedea7badd294dd9',
      '0xfebded1b0986a8ee107f5ab1a1c5a813491deceb',
      '0x149d45c599203e06d727b7d10bb00f7035023932',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-GOOGLx',
      '0x8425b76b81008d6b8f0ed468d72a2098fc13b2d2',
      '0xe92f673ca36c5e2efd2de7628f815f84807e803f',
      '0x8cbbace186e3411b61d57b897c916b912a36991a',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-NVDAx',
      '0x2014e669ab74e114aa155c6b9c162bb26a5b9972',
      '0xc845b2894dbddd03858fd2d643b4ef725fe0849d',
      '0x6540c6b85ade3601f9bdf5b2d8712615dbd7730b',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-QQQx',
      '0x12a81e5b0721e62d907ae5527031fb29f9eef323',
      '0xa753a7395cae905cd615da0b82a53e0560f250af',
      '0xe004a1ff7633a53530e697eec8e9de952d792abb',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-SPCXx',
      '0x405a559b5c0fcb207ddd60dd2b87e9f6b5798564',
      '0x68fa48b1c2fe52b3d776e1953e0e782b5044ce28',
      '0x17beb26401a50d139e167d80e22494e17df040cd',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-TSLAx',
      '0x95603e6d7753833b1ad9feba5f4e7213ba5b1d8a',
      '0x8ad3c73f833d3f9a523ab01476625f269aeb7cf0',
      '0x8f8ce39752ef02916a13422568aea6a4cc178d16',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-PAXG',
      '0xa15100ee8c648dd9d01271424bface4891aa2e8f',
      '0x45804880de22913dafe09f4980848ece6ecbaf78',
      '0xc6e39e6ffc712708d229685fa7ca1abca4309377',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-XAUt',
      '0x894e71b1cdf9fb8775d54dd911d60d5bd2a0e256',
      '0x68749665ff8d2d112fa859aa293f07a622782f38',
      '0x63b102f73c19e6fb0cb3545182d97371dbab62fc',
      6,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-tGLD',
      '0x72bc5907f4bd6963a145cad7fe6538927062bec4',
      '0x6a547b25534234bb79ce6961a23db13de154b6f4',
      '0xecff722c6a57c097b12d5e6a77bd8ad15bae4f0f',
      18,
      259200,
    ],
    [
      'wNLP-DRAMon',
      '0x4aa3d1cca957ee0c21333b7465fc94feab45037a',
      '0xdb5a8f5b4c17f2ee2e7192a150687e48eb3c415d',
      '0x6b5ce3319e47f3c59a27564b5f49ae1926322633',
      18,
      259200,
    ], // unfunded, no APR feed
  ],
  // BNB Chain — 49 pools, 19 funded, 13 with a DefiLlama APR.
  '56': [
    [
      'wNLP-WBNB',
      '0x85f08a266758ea0c98b15deae71ca6ee84392afa',
      '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c',
      '0x9e0de0b11cd3b50370ddabf3926553c4fc3cb956',
      18,
      259200,
    ],
    [
      'wNLP-ETH',
      '0xfafae0cdec65cdd601ffc39f472dd4f5c7bd0721',
      '0x2170ed0880ac9a755fd29b2688956bd959f933f8',
      '0x3906f7078b25b3d4a568438da7be5c0abbe481a8',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-USDT',
      '0xea5ff211ef700dccc521a1e6501c9fe1b95d8ee7',
      '0x55d398326f99059ff775485246999027b3197955',
      '0xb88791ef86d10037f7481b4e3fcbca5bb4162bfa',
      18,
      259200,
    ],
    [
      'wNLP-USDC',
      '0x8592ffff658310150befd83c3c5de326e9bf6f0d',
      '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d',
      '0x38eda6dd1def97aaa07d5fa781395221305bc3f1',
      18,
      259200,
    ],
    [
      'wNLP-USD1',
      '0x2f6f303924f0fdc559c57a14cb73dd9a4d611bd8',
      '0x8d0d000ee44948fc98c9b98a4fa4921476f08b0d',
      '0x92903262eff424cae02d6f7b7b0a486e4c7ef24b',
      18,
      28800,
    ], // 8h queue, 0bips instant fee
    [
      'wNLP-STONEUSD',
      '0xa1cc671f9b975d0a548c436175b99891db961934',
      '0x8b4e28607bdcacbf937f81f29e3dafe7bc1d7c0b',
      '0x5b8382383c7d5bb5341cc3dc97cb09d5b5a5e798',
      18,
      28800,
    ], // unfunded, no APR feed, 8h queue, 0bips instant fee
    [
      'wNLP-CRCLon',
      '0x50fbc992085b065a404fb007e344d49c558047b2',
      '0x992879cd8ce0c312d98648875b5a8d6d042cbf34',
      '0x8a3d397caddd88e725e012af97bb1af3fee3f250',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-GOOGLon',
      '0x14e13011121fa293d20a4d9a7037814f3c932860',
      '0x091fc7778e6932d4009b087b191d1ee3bac5729a',
      '0x0335ab1c86aaea2c442d10fec9e54261a4e0cc90',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-INTCon',
      '0x6b3de4f43d20c7b29bca9850dfbf6bf94824e67e',
      '0xa528caaa2f96090e379d43f90834c75df54d6e74',
      '0x7facfe33b3a0de1ef8cdf15d8717439e9f60cc24',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-MRVLon',
      '0x0d8de51813ef9b8634d3b91086884d3045a5f3e8',
      '0x1501ec83ffef405b4331cc4f73277a40fb0c627d',
      '0x2ac5c050b88a00e395a12b0d724f38a1f32050ad',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-MUon',
      '0x3a18cde8a81060bdc7cace60ecbc2f01765f6008',
      '0x8b6acf6041a81567f012ff6a4c6d96d5818d74bf',
      '0x542c912b0e20895eeba104503d221683e1ceddc9',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-NVDAon',
      '0x493fb39c8e37726539d3fb074c333a1eab58e36b',
      '0xa9ee28c80f960b889dfbd1902055218cba016f75',
      '0x912ce4b093fc7ce0de3cc64e9cd626706da838b7',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-QQQon',
      '0x03e7dce34eed4adc4b228d00a28deba2b7941266',
      '0x0cde6936d305d5b34667fc46425e852efd73559a',
      '0x83ae22182fab9e75f7b85745693fc55100d18ca0',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-SPCXon',
      '0xb810dc8f7ffc2318931b4af211592eac5e1d0190',
      '0xd0a58bc9d88d3ff48c0294cb7e45937d0e41a928',
      '0xcbd0d2627cb03da58dd8ee5bc62c964cb61d4734',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-TSLAon',
      '0x73be73e7570fbcbd3d83de1e32ae5594c3963fe1',
      '0x2494b603319d4d9f9715c9f4496d9e0364b59d93',
      '0xbf9640dd79e916f386e2c89566654b6fe16b0f65',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-DRAMon',
      '0x90cc94befe6c6437de7c6ded32a4dc25ebbee3d2',
      '0x087b5761b161429013d41ea54cd2fb6022a21564',
      '0x8ccb472112d0fe00ba7f8b8b9fc407e68d78f62b',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-CRCLx',
      '0xddfe8d98a0b459a2a2906e02ab3dd5a5bbc303c8',
      '0xfebded1b0986a8ee107f5ab1a1c5a813491deceb',
      '0x4d722dc923510a9ec4cfb3c41c414b15cbce4df0',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-GOOGLx',
      '0xa8d9eb043d4bc05e4b0dac89cd5837cf40fb3bc5',
      '0xe92f673ca36c5e2efd2de7628f815f84807e803f',
      '0x2db1571c7c7954e546c8f0870cfa16b244b7f6ff',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-NVDAx',
      '0x358100ed7ecdd6c608d9d5e642e34b1a2ba152b7',
      '0xc845b2894dbddd03858fd2d643b4ef725fe0849d',
      '0xef435b99c2108d0309211d0cc05b70d202c0b5df',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-QQQx',
      '0x427fb58709c47fb8794771bd2e17784c0cda845a',
      '0xa753a7395cae905cd615da0b82a53e0560f250af',
      '0x7768aa6322ab912bee159191ec3380df13f6db37',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-SPCXx',
      '0x335d144f211c3f4dedb3ef1fe003cbc7dd9c24a9',
      '0x68fa48b1c2fe52b3d776e1953e0e782b5044ce28',
      '0xee880bf2050a23bb84dd4734dd7413d9d9793500',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-TSLAx',
      '0xbcac5180fc867d741d06e1aafde15d65ca16d3f3',
      '0x8ad3c73f833d3f9a523ab01476625f269aeb7cf0',
      '0x826d54e624c59a01fe2917cda2429d3058880099',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-A4B',
      '0xd8a25f5e864534bb2324f2eff6f5c3dd58693b8b',
      '0x75fd4cf6f8392e41e70391d60c90c0d5211603a1',
      '0xf4166b08dc7eea43101066ef8f52fb95d994d62c',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-COINB',
      '0x16ccbca076c24d1c174cddc8ce30389097bf8982',
      '0x585bde7c54abb5ccd7791f923d6c2187635f3952',
      '0xe7290a875d32f676b715283e1eafdfcece97110b',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-C4B',
      '0xe05f1635756b6f5046e53f8e911a14794a73a4e7',
      '0x80f3d493ebce97e343c53d29a137942416b4ffc0',
      '0x9d2f7e285557c65416fb2bbb268d509e9e2eb2b3',
      18,
      259200,
    ],
    [
      'wNLP-DRAMB',
      '0x52207f4c347631abb40484e30c71fa66660581a7',
      '0x93862d63fd9fd488b1328e9b47717d75e994a84b',
      '0xd805265e8e1c003c65d7e2565149289459b2911f',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-E3B',
      '0x3dac466a0190ce1dd7a062290ce48310206b4bca',
      '0xbe82f76637dba2c114c41df856c2c51e522e2cb8',
      '0x50b91e72b7a4a5eaf926931e4a6b3357c26235b2',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-GLWB',
      '0xfc6fdde35860a327be88f54bf35993f72de91a87',
      '0x740e075cbbea22a082b9d6679e65e82767875b6a',
      '0xbe38df1755e942e11241c2a39e029f9359a483e3',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-GOOGLB',
      '0x38982f1604d2459f317ca9ff03808b17d4d9754b',
      '0x3f53de71c126bdabae20f9cd64848d317f6c3238',
      '0x7de2d4edc91dbef780f0c4f59828e752a9f43bac',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-I4B',
      '0x737b83b17156348b84157d528e02bd9acec4163a',
      '0xe614e2fc6c787035ff51f452e8e826bfd32d5283',
      '0xe31013938457f086958c32a921a6ec17f468902d',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-L4B',
      '0xcf76092d538e4df7a1e8dc3eb8c21be1d462a5da',
      '0x64748bea17b6d19e242adf20425de2440c656142',
      '0xa97aea8e9d81771d4566cd1a636b58b600a1e3e8',
      18,
      259200,
    ],
    [
      'wNLP-M5B',
      '0x25fe9180e6c95da24b7a3fcff8bf2dbca1fb171a',
      '0x7425889fe94f9d693e8daefe88bcced6acfef4c0',
      '0x4c234c7575f762066b59670d52478c12a1d2c0ff',
      18,
      259200,
    ],
    [
      'wNLP-M6B',
      '0x19bbae8f804b0ea4c7c4351f722fd39efe37c2b2',
      '0x80106cb3ead06659a5ad19df39d9b4733863b9b0',
      '0x94cd73e04503537da2aa129832a0e3580d8f76a3',
      18,
      259200,
    ],
    [
      'wNLP-M4B',
      '0xafcd64ebfc83d57ddf5716f3814019f5543d7c46',
      '0xe87afb3076aeb0f9b14e368de8145ae6a2826a14',
      '0x8729f978393fe4a71a819c392822f80a0600f260',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-M2B',
      '0x66924f881bd0cbfabded19838c5f5443867359d9',
      '0xcdf2f3e0fa43c47a6662a91c9e4a7c5f69762699',
      '0x6285d06426eef9beef33ee92eab7f491f736da57',
      18,
      259200,
    ],
    [
      'wNLP-NBISB',
      '0x4d45a527b03551821c8d7d8974b939ed6a2824ec',
      '0xe256bc2a4f5297f8ba6f043f180a46300ecbcbb1',
      '0x1bbdd8da349ba112b51e644f10bab6598db4330b',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-N4B',
      '0x2ba837484e8969da87d237effedea7badd294dd9',
      '0x02fca66c1d1afb4e2a7884261eb00f63598a7436',
      '0x149d45c599203e06d727b7d10bb00f7035023932',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-P4B',
      '0x93da79355ae801a673e4d3b656a4152330646ee4',
      '0x0ca5d51d0277bd006fd9607d3e560785ebad8222',
      '0xe5ed3ae9fca303126526d4084481fd0b9f589a97',
      18,
      259200,
    ],
    [
      'wNLP-QCOMB',
      '0xdfa34465d1c6c044304b798fa4a147eac579b66b',
      '0x5f7a56e877b9130608bf8be962621011182fefe1',
      '0x8618171d00f972b6b40a7c20dd9f9b3d6efdb673',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-Q3B',
      '0x564d609b363b902148af559c18aa1bc84bf29a5b',
      '0x205812cdbed920aff76c6580abd681a46d11efc7',
      '0xbb389a0ae36d5dc04967b1866c8f9835eca9f895',
      18,
      259200,
    ],
    [
      'wNLP-S4B',
      '0x2014e669ab74e114aa155c6b9c162bb26a5b9972',
      '0x3ee4df61bd4f867e349beae8bfe07bc31b4850fb',
      '0x6540c6b85ade3601f9bdf5b2d8712615dbd7730b',
      18,
      259200,
    ],
    [
      'wNLP-SOXLB',
      '0xcd1c23eaec3bbdef5f945f2b352b188c8a3b14d3',
      '0xd97d097a89113fa59b76c572e5b2eb647e8eefaf',
      '0x7ee63c39be8f868991830f47b167c21c0c1bf23c',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-S5B',
      '0xf27760f7b431ad8caee0f4e3062e6140b14e0ebc',
      '0xbe9d156892e55e7154bcd3cb0fea677f9d3103e1',
      '0x8386c857577fced92537aea6e5112f38da46212a',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-SPYB',
      '0x5c53355123a51bd800934e0fa1f8eb8c9f3a848b',
      '0x7138b48df7d98d7e3cc221bfe7192d0a178182d8',
      '0x1535fa32cd0bcf66309da5209dbfca4f4c466ce1',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-T4B',
      '0xaeb7c9122d897a224180d2ca25bc86a16d12ae92',
      '0x5b1910eaad6450e50f816082aa078c41f10c292f',
      '0x5b2bc2d8ad715391aff2ffa0f3f19d3a4c6686ed',
      18,
      259200,
    ],
    [
      'wNLP-WDCB',
      '0x3bdd26cf07bd97baa5f2a588cb6a67b83c7a60a5',
      '0xebe29695f8047c13d36e7a790ca8c1b239ffad1c',
      '0x22e87beb7307dcac4ab20b4a23080a3b7a078ff5',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-CANP',
      '0x63689d5fe0d8f766fc11296c694634cd6f14ee6e',
      '0xdc22f0a9a60eee3697e925414bdb4580d1c35f91',
      '0xed2f65b5889f9889ad45b97108cc1d449ddef6ff',
      6,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-CBRSB',
      '0x01246b4a5e22bba113108606451db0817a7d20f1',
      '0xe81c6bb0266cd68b4f17278531dd03ea1f12da4e',
      '0xb25861953cc45c6a0a572f892eb671e00198cfc7',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-SK4B',
      '0x31ac519953055b7c356228658e6836d1c7b4fa36',
      '0xca750ef65f295bbecd685abf54e82caf297bdb61',
      '0x5e043dc607c23b94b964c8553722932b9eaf54cb',
      18,
      259200,
    ], // unfunded, no APR feed
  ],
  // Arbitrum — 4 pools, 3 funded, 2 with a DefiLlama APR.
  '42161': [
    [
      'wNLP-WETH',
      '0x9db777ca39e1f1144d564cb5f593d629d2debe46',
      '0x82af49447d8a07e3bd95bd0d56f35241523fbab1',
      '0x7f1bcc60ed3c80da906fd91a2ec63ec71442430a',
      18,
      259200,
    ],
    [
      'wNLP-USDT',
      '0xe83ce032108f539c5a5b1c6029a89ff412d9e27f',
      '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9',
      '0xa6734c9588c458f4f6bd450a62245d76303fb9a3',
      6,
      259200,
    ],
    [
      'wNLP-USDC',
      '0xb873363be6e4c99c1f4e6ffd6526c7c03a7a07ad',
      '0xaf88d065e77c8cc2239327c5edb3a432268e5831',
      '0x73a8df5258fb736eb1aeb198b72b73d6eb457bf8',
      6,
      259200,
    ], // no APR feed
    [
      'wNLP-WBTC',
      '0xd8865923465463002bc2ccc522e5a21f8d06adfa',
      '0x2f2a2543b76a4166549f7aab2e75bef0aefc5b0f',
      '0xb043e07edf8cf2ddc2fa22bb087697a721af1058',
      8,
      259200,
    ], // unfunded, no APR feed
  ],
  // Base — 4 pools, 3 funded, 2 with a DefiLlama APR.
  '8453': [
    [
      'wNLP-WETH',
      '0xc750fe788a828dc960cd08e8bbe1ddd3bffc03bf',
      '0x4200000000000000000000000000000000000006',
      '0x9af2f3c0cd35283e13f7087e2b34b1444b57a44c',
      18,
      259200,
    ],
    [
      'wNLP-USDC',
      '0xe745b0565cecb4612a41eae78319bf3ca8063e6f',
      '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
      '0xe7d6b7689bd2cea0af533dd3ad79df6968acb53e',
      6,
      259200,
    ],
    [
      'wNLP-USDT',
      '0x0872c63c9b1f55ea4166752ba3b890a444b59e9e',
      '0xfde4c96c8593536e31f229ea8f37b2ada2699bb2',
      '0x337ec533b568957af458e10cbfbcbf3d59c935b1',
      6,
      259200,
    ], // no APR feed
    [
      'wNLP-cbBTC',
      '0x90256c018d2acadda2da0b4fd61449e956fd59b6',
      '0xcbb7c0000ab88b473b1f5afd9ef808440eed33bf',
      '0xa2efe8e37f247cb51c2a48692301335292ccb630',
      8,
      259200,
    ], // unfunded, no APR feed
  ],
  // Monad — 6 pools, 4 funded, 0 with a DefiLlama APR.
  '143': [
    [
      'wNLP-WMON',
      '0x0166f153e26f572dfac570ccc351ba84ac7a2a72',
      '0x3bd359c1119da7da1d913d1c4d2b7c461115433a',
      '0x277ad957898ce34fd959cbfd28ab5962de613b85',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-USDC',
      '0xf3d44492f11a2dc234acba80a9e377ab5732b09d',
      '0x754704bc059f8c67012fed69bc8a327a5aafb603',
      '0xefadd9a8342bd4ae4d55aed2d748dc006149186a',
      6,
      259200,
    ], // no APR feed
    [
      'wNLP-USDT0',
      '0x3b1dbe5ca2d39fac168e3575d707b714a7eaa7e7',
      '0xe7cd86e13ac4309349f30b3435a9d337750fc82d',
      '0x4cbf9530d653d862a8ece9ed46af6c2f136e2e80',
      6,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-WETH',
      '0x01135c821a87154de3f338f1978811f2648e9570',
      '0xee8c0e9f1bffb4eb878d8f15f368a02a35481242',
      '0x92e6d5cdf9c01473adf7089cc416bb934f702cfd',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-cbBTC',
      '0x72c486886d199984bdbcf278ba68358365b0f8b7',
      '0xd18b7ec58cdf4876f6afebd3ed1730e4ce10414b',
      '0x339808a8be84f74bdcd570c546e2efbd0e780296',
      8,
      259200,
    ], // no APR feed
    [
      'wNLP-AUSD',
      '0x70dd4db19f75dfea083ce50e77f9b53b55d8558a',
      '0x00000000efe302beaa2b3e6e1b18d08d69a9012a',
      '0x4ee1c93eb1ca08f566211fb18a8dc5c1c6d04939',
      6,
      28800,
    ], // unfunded, no APR feed, 8h queue
  ],
  // X Layer — 9 pools, 2 funded, 0 with a DefiLlama APR.
  '196': [
    [
      'wNLP-WETH',
      '0xde540eabdb4ee3a2680394a4346dbfd76bb78e83',
      '0x5a77f1443d16ee5761d310e38b62f77f726bc71c',
      '0x79292d171531673ff97035315fda568189c3c8a5',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-USD₮0',
      '0x0b28888e8f0755809c679e01f67591d1ed5ae9b9',
      '0x779ded0c9e1022225f8e0630b35a9b54be713736',
      '0x4af9206a885e4708ad3be482611d224239efe173',
      6,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-USDG',
      '0x6285d06426eef9beef33ee92eab7f491f736da57',
      '0x4ae46a509f6b1d9056937ba4500cb143933d2dc8',
      '0x92e6d5cdf9c01473adf7089cc416bb934f702cfd',
      6,
      259200,
    ], // no APR feed
    [
      'wNLP-NVDAx',
      '0x7fde95f8ca333f87677e89e42d09cb4b156f8921',
      '0xc845b2894dbddd03858fd2d643b4ef725fe0849d',
      '0x06ab0323c1e3759fe3df946068825fe572db1254',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-TSLAx',
      '0x78901dd5e09434c6d8c4caa651c012280324af59',
      '0x8ad3c73f833d3f9a523ab01476625f269aeb7cf0',
      '0x9dc27e2b3cc52533c159567a9e45243014ef3c0d',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-CRCLx',
      '0xe1e4bcb64121fff9cfa4d4f5bc31759f035992ad',
      '0xfebded1b0986a8ee107f5ab1a1c5a813491deceb',
      '0xf3d44492f11a2dc234acba80a9e377ab5732b09d',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-GOOGLx',
      '0x1d2831785db72d0809d493a4cfcc90fee47927ad',
      '0xe92f673ca36c5e2efd2de7628f815f84807e803f',
      '0xc9be6db8797f44455e578cac759cd209a1fd126d',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-QQQx',
      '0x4fb706eb3e3784da4fc5aa824886f6129a65fb2e',
      '0xa753a7395cae905cd615da0b82a53e0560f250af',
      '0x4f53c62a940748dd572d5ac790d3628e9b39ca8e',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-SPCXx',
      '0x6540c6b85ade3601f9bdf5b2d8712615dbd7730b',
      '0x68fa48b1c2fe52b3d776e1953e0e782b5044ce28',
      '0x277ac2538df35713f196d75058e6a661a058cd6f',
      18,
      259200,
    ], // unfunded, no APR feed
  ],
  // Robinhood Chain — 10 pools, 2 funded, 0 with a DefiLlama APR.
  '4663': [
    [
      'wNLP-WETH',
      '0x7ce2c710c132f68a74dbb186644ec1eb86dfaf4e',
      '0x0bd7d308f8e1639fab988df18a8011f41eacad73',
      '0x70f167a4fed2c68d68760a4b0b6e93d919cfa935',
      18,
      259200,
    ], // no APR feed
    [
      'wNLP-USDG',
      '0xbb761ba477de2e887968d6c01073a477a9f9815b',
      '0x5fc5360d0400a0fd4f2af552add042d716f1d168',
      '0x855a737dbaf7143574cfe869b0849f929d8ba222',
      6,
      259200,
    ], // no APR feed
    [
      'wNLP-AMD',
      '0x1eda0a1353c57113ff41c32f38ca4b8d0cfec562',
      '0x86923f96303d656e4aa86d9d42d1e57ad2023fdc',
      '0x24417d45dfe01841b5f28c05c1d44abf4c321ae6',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-CRCL',
      '0x965cfe674859cb223ffdc0f72722ad5619f7d90f',
      '0xdf0992e440dd0be65bd8439b609d6d4366bf1cb5',
      '0x3581a66d36e307b41b11402cf858f3f83e31d859',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-MSFT',
      '0xa43ab4c0539ad04b41203fc0544d859540088613',
      '0xe93237c50d904957cf27e7b1133b510c669c2e74',
      '0xa81bfbe0994e0b49d66f2273c52d5d447ea71038',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-MU',
      '0xb05b806fc5bdce389b734dce50400fa27ae54942',
      '0xff080c8ce2e5feadaca0da81314ae59d232d4afd',
      '0x48cf773961339cea5284f62bcc9a4a342be42adc',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-NVDA',
      '0x001660ed89e93c7c9b348e247934fa1ebc3a2625',
      '0xd0601ce157db5bdc3162bbac2a2c8af5320d9eec',
      '0x6422b1130526d842f74a9340a0d244c289534dd9',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-SNDK',
      '0xbd254986706115081d84b7a71f3aa1f07d748a5b',
      '0xb90a19ff0af67f7779aff50a882a9cff42446400',
      '0x45ee33d6c6d399aa6fa14e25c5462bfd7f0ffea9',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-SPCX',
      '0x68722297b35760ff16b873c4c89187f4e1d5dbe6',
      '0x4a0e65a3eccec6dbe60ae065f2e7bb85fae35eea',
      '0x2933c9c18ba62fe494b4ac75d32060594c9c28bc',
      18,
      259200,
    ], // unfunded, no APR feed
    [
      'wNLP-TSLA',
      '0xf54b421461d41203e4babdc59d1259d7de886a73',
      '0x322f0929c4625ed5bad873c95208d54e1c003b2d',
      '0x0945712145b562ec6ba6631293fb917feb6b262a',
      18,
      259200,
    ], // unfunded, no APR feed
  ],
  // Morph — 2 pools, 2 funded, 0 with a DefiLlama APR.
  '2818': [
    [
      'wNLP-USDC',
      '0xcf7b55c544b579a09b54f5a4da3e1ba476c04364',
      '0xcfb1186f4e93d60e60a8bdd997427d1f33bc372b',
      '0x26ff7df6d9e1feead7786a6ba82618aafd7c8ea7',
      6,
      259200,
    ], // no APR feed
    [
      'wNLP-BGBTC',
      '0xca0f3fb46eb413abefa10d91aa2a6fd49eab60eb',
      '0x31011317764e097b28d159a8145b92bfa453f606',
      '0x0d8de51813ef9b8634d3b91086884d3045a5f3e8',
      8,
      259200,
    ], // no APR feed
  ],
}

/**
 * Expanded entries per chain. Uniform across every row: brand `Native`,
 * non-rebasing share, permissionless mint (fork-verified), the
 * `native-wnlp` reader, and the per-chain DefiLlama APR fetcher — absent
 * on Monad / X Layer / Robinhood / Morph, which DefiLlama does not index,
 * so those report `supplyRate: 0`.
 */
export const NATIVE_WNLP_ENTRIES: {
  [chainId: string]: SavingsRegistryEntry[]
} = Object.fromEntries(
  Object.entries(NATIVE_WNLP_ROWS).map(([chainId, rows]) => [
    chainId,
    rows.map(
      ([
        symbol,
        address,
        underlying,
        withdrawQueue,
        decimals,
        withdrawalWindowSeconds,
      ]): SavingsRegistryEntry => ({
        address,
        underlying,
        symbol,
        brand: 'Native',
        description:
          "Native Credit Pool: deposits fund a credit line that KYB'd market makers draw to quote on Native's order book, and yield is a rebate on their trading fees — not loan interest. The share price is operator-pushed with no on-chain solvency invariant; exit instantly at a fee (capped by the chain's CreditVault inventory) or free through the withdrawal queue.",
        decimals,
        isRebasing: false,
        isMintable: true,
        // The credit line is granted OFF-CHAIN to KYB'd market makers — there
        // is no collateral on-chain to liquidate if one does not repay.
        solvency: 'undercollateralized',
        withdrawalMode: 'fee-or-queued',
        withdrawalCooldownSeconds: withdrawalWindowSeconds,
        reader: 'native-wnlp',
        withdrawQueue,
        inventoryContract: NATIVE_CREDIT_VAULT[chainId],
        yieldFetcher: nativeCreditPoolFetcher(chainId),
        yieldKey: nativeCreditPoolYieldKey(chainId, underlying),
      }),
    ),
  ]),
)
